import type { RemoteThreadListAdapter, ThreadMessage } from '@assistant-ui/react'
import { createAssistantStream } from 'assistant-stream'
import { setActiveRemoteThreadId } from '@/runtime/activeThreadId'
import api from '@/services/api'
import type { ThreadListApiResponse, ThreadResponse } from '@/types/chat'

function toMetadata(thread: ThreadResponse) {
  const lastMessageAt = new Date(thread.last_message_at)
  const createdAt = new Date(thread.created_at)
  return {
    remoteId: thread.id,
    status: thread.archived ? ('archived' as const) : ('regular' as const),
    title: thread.title || 'New chat',
    lastMessageAt: Number.isNaN(lastMessageAt.getTime()) ? createdAt : lastMessageAt,
    custom: {
      last_message_at: thread.last_message_at,
      created_at: thread.created_at,
    },
  }
}

function extractTextFromContent(content: unknown): string {
  if (typeof content === 'string') {
    return content.trim()
  }
  if (!Array.isArray(content)) {
    return ''
  }
  return content
    .map((part) => {
      if (!part || typeof part !== 'object') return ''
      const record = part as { type?: unknown; text?: unknown }
      if (record.type === 'text' && typeof record.text === 'string') {
        return record.text
      }
      return ''
    })
    .join('\n')
    .trim()
}

function messagesForTitle(messages: readonly ThreadMessage[]) {
  return messages.map((message) => ({
    role: message.role,
    content: extractTextFromContent(message.content),
  }))
}

export const threadListAdapter: RemoteThreadListAdapter = {
  async list() {
    const { data } = await api.get<ThreadListApiResponse>('/threads', {
      params: { include_archived: false },
    })
    // Hide empty drafts (created but never titled / never asked a question).
    const threads = data.threads.filter((thread) => Boolean(thread.title?.trim()))
    return {
      threads: threads.map(toMetadata),
    }
  },

  async initialize(_localId: string) {
    const { data } = await api.post<ThreadResponse>('/threads', {})
    setActiveRemoteThreadId(data.id)
    return { remoteId: data.id }
  },

  async rename(remoteId: string, newTitle: string) {
    await api.patch(`/threads/${remoteId}`, { title: newTitle })
  },

  async archive(remoteId: string) {
    await api.post(`/threads/${remoteId}/archive`)
  },

  async unarchive(remoteId: string) {
    await api.post(`/threads/${remoteId}/unarchive`)
  },

  async delete(remoteId: string) {
    await api.delete(`/threads/${remoteId}`)
  },

  async fetch(threadId: string) {
    const { data } = await api.get<ThreadResponse>(`/threads/${threadId}`)
    return toMetadata(data)
  },

  async generateTitle(remoteId, messages) {
    const { data } = await api.post<ThreadResponse>(
      `/threads/${remoteId}/title`,
      { messages: messagesForTitle(messages) },
    )
    const title = data.title || 'New chat'
    return createAssistantStream((controller) => {
      controller.appendText(title)
    })
  },
}
